import { FC } from "react";
import { Box, Tooltip } from "@mui/material";

interface EmojiBadgeProps {
	value: string;
	values: string[];
}

// Maps backend emoji values (auto-generated "1"/"2"/"3" for smileys events,
// or explicit labels like "happy"/"neutral"/"sad" for SOWS Page-2 style) to
// visible glyphs and a tone. For numeric values, we treat the lowest index
// as "best" (1 = green smile), middle as neutral, last as "worst" (red sad).
const NUMERIC_GLYPHS = ["🙂", "😐", "🙁", "😢", "😡"];
const NAMED_GLYPHS: Record<string, { glyph: string; label: string }> = {
	happy: { glyph: "🙂", label: "Happy" },
	neutral: { glyph: "😐", label: "Neutral" },
	sad: { glyph: "🙁", label: "Sad" },
};

const TONES = ["#1D9E75", "#F2A93B", "#D85A30"];

const resolveGlyph = (
	value: string,
	values: string[],
): { glyph: string; label: string; tone: string } => {
	const named = NAMED_GLYPHS[value.toLowerCase()];
	if (named) {
		const idx = values.findIndex(
			(v) => v.toLowerCase() === value.toLowerCase(),
		);
		return {
			glyph: named.glyph,
			label: named.label,
			tone: TONES[Math.min(idx >= 0 ? idx : 0, TONES.length - 1)],
		};
	}
	const idx = values.findIndex((v) => v === value);
	const glyphIdx = idx >= 0 ? idx : 0;
	return {
		glyph: NUMERIC_GLYPHS[glyphIdx] ?? value,
		label: value,
		tone: TONES[Math.min(glyphIdx, TONES.length - 1)],
	};
};

const EmojiBadge: FC<EmojiBadgeProps> = ({ value, values }) => {
	const { glyph, label, tone } = resolveGlyph(value, values);
	return (
		<Tooltip title={label} arrow>
			<Box
				role="img"
				aria-label={label}
				sx={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: 24,
					height: 24,
					fontSize: 18,
					color: tone,
				}}
			>
				{glyph}
			</Box>
		</Tooltip>
	);
};

export default EmojiBadge;
